import React from "react";
import { Modal, ModalFuncProps, Space, Table, Tooltip, Typography } from "antd";
import { Inventory } from "../../api/inventory";
import { startCase } from "lodash-es";
import { ExpandAltOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { EditInventory } from "./ProductInventoryTable";
import moment from "moment";

type ColumnType = ReturnType<typeof getColumns>;

export type Option = {
  value: string;
  label: string;
};

export type InventoryTableData = Omit<Inventory, "facilities"> & { facilities: Option[] };

interface OrdersModalProps extends ModalFuncProps {
  orders: Inventory["reserved_orders" | "orders_to_be_reserved"];
}

const MODAL_COLUMNS = [
  {
    title: "Order",
    dataIndex: "order_id",
    key: "id",
    render(number: string, { id }: OrdersModalProps["orders"][0]) {
      return (
        <Typography.Link href={`/fulfillment/orders/${id}`} target="_blank">
          {number}
        </Typography.Link>
      );
    },
  },
  { title: "Quantity", dataIndex: "item_quantity", key: "item_quantity" },
];

export const getOrdersModal = (props: OrdersModalProps) => {
  return Modal.info({
    icon: false,
    closable: true,
    okButtonProps: { hidden: true },
    ...props,
    content: <Table dataSource={props.orders} columns={MODAL_COLUMNS} pagination={false} size="small" />,
  });
};

export const modalContext = (key: "reserved" | "to_be_reserved") => {
  /* eslint-disable react/display-name */
  return (quantity: number, inventory: InventoryTableData) => {
    const facility = inventory.facilities.map((f) => f.label.toUpperCase());
    const title = `${facility} (${startCase(key)})`;
    const orders = inventory[key === "reserved" ? "reserved_orders" : "orders_to_be_reserved"] || [];

    if (!quantity || !orders.length) return quantity;
    return (
      <Space size="small" onClick={() => getOrdersModal({ title, orders })}>
        <Tooltip title="Click to see linked orders">
          <Typography.Link>{quantity}</Typography.Link>
          <ExpandAltOutlined />
        </Tooltip>
      </Space>
    );
  };
};

const sorter = (key: keyof InventoryTableData) => {
  return {
    compare: (a: InventoryTableData, b: InventoryTableData) => +a[key] - +b[key],
  };
};

export const redactedColumns = (cols: ColumnType) => {
  const redactedColumnKeys = ["in_transit", "on_hold", "damaged"];
  return cols.map((column) => {
    if (column.key && redactedColumnKeys.includes(column.key)) {
      return { ...column, render: () => "-" };
    }
    return column;
  });
};

export const getColumns = (allowEditOnHand: boolean, showAvailableForPicking: boolean, newInventory: boolean) => {
  const columns = [
    {
      title: "Warehouse",
      dataIndex: "facilities",
      key: "facility",
      render(facilities: InventoryTableData["facilities"]) {
        return facilities.map((f) => f.label);
      },
    },
    {
      title: "On Hand",
      dataIndex: "on_hand",
      key: "on_hand",
      sorter: sorter("on_hand"),
      render(onHand: number, inventory: InventoryTableData) {
        if (!allowEditOnHand) return onHand;
        return <EditInventory inventory={inventory} />;
      },
    },
    {
      title: "Available For Sale",
      dataIndex: "available_for_sale",
      key: "available_for_sale",
      sorter: sorter("available_for_sale"),
    },
    {
      title: newInventory ? "Processing" : "Reserved",
      dataIndex: newInventory ? "processing" : "reserved",
      key: newInventory ? "processing" : "reserved",
      sorter: sorter(newInventory ? "processing" : "reserved"),
      ...(newInventory ? {} : { render: modalContext("reserved") }),
    },
    {
      title: `To be ${newInventory ? "Processed" : "Reserved"}`,
      dataIndex: newInventory ? "to_be_processed" : "to_be_reserved",
      key: newInventory ? "to_be_processed" : "to_be_reserved",
      sorter: sorter(newInventory ? "to_be_processed" : "to_be_reserved"),
      ...(newInventory ? {} : { render: modalContext("to_be_reserved") }),
    },
    {
      title: "Damaged",
      dataIndex: "damaged",
      key: "damaged",
      sorter: sorter("damaged"),
    },
    {
      title: "On Hold",
      dataIndex: "on_hold",
      key: "on_hold",
      sorter: sorter("on_hold"),
    },
    {
      title: "Incoming",
      dataIndex: "incoming",
      key: "incoming",
      sorter: sorter("incoming"),
    },
    {
      title: "Backorder",
      dataIndex: "backorder",
      key: "backorder",
      sorter: sorter("backorder"),
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value: string) => (value ? moment(value).format("ll LTS") : "-"),
    },
  ];

  if (!newInventory) {
    columns.splice(5, 0, {
      title: "In Transit",
      dataIndex: "in_transit",
      key: "in_transit",
      sorter: sorter("in_transit"),
    });

    if (showAvailableForPicking) {
      columns.splice(3, 0, {
        title: "Available for Picking",
        dataIndex: "available_for_work_orders",
        key: "available_for_work_orders",
        sorter: sorter("available_for_work_orders"),
      });
    }
  }

  if (newInventory) {
    columns.splice(2, 0, {
      title: "Available To Pick",
      dataIndex: "available_to_pick",
      key: "available_to_pick",
      sorter: sorter("available_to_pick"),
    });
    columns.splice(4, 0, {
      title: "Pending",
      dataIndex: "pending",
      key: "pending",
      sorter: sorter("pending"),
    });
  }

  return columns;
};

export const renderTooltip = (title: string, tooltipText: string) => (
  <div>
    {title}{" "}
    <Tooltip title={tooltipText} placement="right">
      <QuestionCircleOutlined style={{ fontSize: "14px" }} />
    </Tooltip>
  </div>
);
